import React, { useContext, useEffect, useState, useMemo } from "react";
// Wraps react-router-dom's Link to confirm before leaving a page with
// unsaved changes (see src/context/unsavedChangesGuard.js).
import RouterLink from "../Misc/GuardedRouterLink/GuardedRouterLink";

// CONTEXT
import { UserContext } from "../../context/UserContext";

// CSS
import "./Navigation.css";
import j2logo from "../../images/j2_logo_white.png";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import {
	Collapse,
	ImageListItem,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
} from "@mui/material";

// MUI ICONS
import {
	AdminPanelSettings as AdminPanelSettingsIcon,
	Article as ArticleIcon,
	AttachEmail as AttachEmailIcon,
	AutoStories as AutoStoriesIcon,
	Badge as BadgeIcon,
	Build as BuildIcon,
	Category as CategoryIcon,
	CollectionsBookmark as CollectionsBookmarkIcon,
	Construction as ConstructionIcon,
	CreateNewFolder as CreateNewFolderIcon,
	DensitySmall as DensitySmallIcon,
	DriveFileRenameOutline as DriveFileRenameOutlineIcon,
	ExpandLess,
	ExpandMore,
	Hardware as HardwareIcon,
	HistoryEdu as HistoryEduIcon,
	HomeWork as HomeWorkIcon,
	HouseSiding as HouseSidingIcon,
	ImportantDevices as ImportantDevicesIcon,
	Inventory as InventoryIcon,
	LaptopMac as LaptopMacIcon,
	Logout as LogoutIcon,
	ListAlt as ListAltIcon,
	LowPriority as LowPriorityIcon,
	ManageAccounts as ManageAccountsIcon,
	MenuBook as MenuBookIcon,
	NewReleases as NewReleasesIcon,
	NoEncryptionGmailerrorred as NoEncryptionGmailerrorredIcon,
	Pending as PendingIcon,
	PublishedWithChanges as PublishedWithChangesIcon,
	Reviews as ReviewsIcon,
	ScatterPlot as ScatterPlotIcon,
	QueryStats as QueryStatsIcon,
	Work as WorkIcon,
	Merge as MergeIcon,
	SendAndArchive as SendAndArchiveIcon,
	ViewModule as ViewModuleIcon,
	Layers as LayersIcon,
} from "@mui/icons-material";
import TaskIcon from "@mui/icons-material/Task";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DomainIcon from "@mui/icons-material/Domain";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventBusyIcon from "@mui/icons-material/EventBusy";

// CONSTANTS

// REACT COMPONENT
const Navigation = (props) => {
	// MSAL
	const { instance } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATES
	// Nav Collapse
	const [navExpanded, setNavExpanded] = useState(
		Boolean(props.forceExpanded),
	);
	const [navListID, setNavListID] = useState(
		props.forceExpanded ? "nav-list" : "nav-list-compressed",
	);

	const [delayHandler, setDelayHandler] = useState(null);

	// Dropdowns
	const [proposalsOpen, setProposalsOpen] = useState(false);
	const [projectCoordinationOpen, setProjectCoordinationOpen] =
		useState(false);
	const [logsOpen, setLogsOpen] = useState(false);
	const [adminOpen, setAdminOpen] = useState(false);
	const [specificationsOpen, setSpecificationsOpen] = useState(false);
	const [profileOpen, setProfileOpen] = useState(false);
	const [hardwareInventoryOpen, setHardwareInventoryOpen] = useState(false);
	const mobileOnly = Boolean(props.mobileOnly);

	useEffect(() => {
		if (props.forceExpanded) {
			setNavExpanded(true);
			setNavListID("nav-list");
		} else {
			setNavExpanded(false);
			setNavListID("nav-list-compressed");
		}
	}, [props.forceExpanded]);

	// INPUT HANDLERS
	const proposalDropdownClicked = () => {
		setProposalsOpen(!proposalsOpen);
	};

	const projectCoordinationDropdownClicked = () => {
		setProjectCoordinationOpen(!projectCoordinationOpen);
	};

	const logsDropdownClicked = () => {
		setLogsOpen(!logsOpen);
	};

	const adminDropdownClicked = () => {
		setAdminOpen(!adminOpen);
	};

	const specificationsDropdownClicked = () => {
		setSpecificationsOpen(!specificationsOpen);
	};

	const profileDropdownClicked = () => {
		setProfileOpen(!profileOpen);
	};

	const hardwareInventoryDropdownClicked = () => {
		setHardwareInventoryOpen(!hardwareInventoryOpen);
	};

	const signOutPressed = () => {
		instance.logoutRedirect({
			postLogoutRedirectUri: "/",
			onRedirectNavigate: (url) => {
				// Return false if you would like to stop navigation after local logout
				return false;
			},
		});
	};

	// Nav Collapse Handlers
	const mouseLeaveNav = () => {
		if (props.disableHover) {
			return;
		}

		clearTimeout(delayHandler);

		setNavExpanded(false);
		setNavListID("nav-list-compressed");
	};

	const mouseEnterNav = () => {
		if (props.disableHover) {
			return;
		}

		setDelayHandler(
			setTimeout(() => {
				setNavExpanded(true);
				setNavListID("nav-list");
			}, 250), // Only expand after mouse has been over the nav for 250ms
		);
	};

	// RETURN
	return (
		<Collapse
			in={navExpanded}
			id="nav-collapse"
			orientation="horizontal"
			collapsedSize="var(--nav-collapsed-width)"
			onMouseEnter={mouseEnterNav}
			onMouseLeave={mouseLeaveNav}
			timeout={250}
		>
			<div className="Navigation">
				<List id={navListID} component="nav">
					<div id="nav-list-items">
						{/* NAV LOGO */}
						<ImageListItem id="nav-logo-container">
							<img alt="j2 logo" src={j2logo} id="nav-logo" />
						</ImageListItem>

						{mobileOnly && (
							<>
								<ListItemButton
									component={RouterLink}
									to="/"
									className="nav-list-item-button"
								>
									<ListItemIcon className="nav-list-item-icon">
										<CreateNewFolderIcon
											style={{ color: "white" }}
										/>
									</ListItemIcon>
									<ListItemText
										primary="RFP"
										className="nav-list-item-text"
										disableTypography
									/>
								</ListItemButton>
								<ListItemButton
									component={RouterLink}
									to="/rfa"
									className="nav-list-item-button"
								>
									<ListItemIcon className="nav-list-item-icon">
										<ConstructionIcon
											style={{ color: "white" }}
										/>
									</ListItemIcon>
									<ListItemText
										primary="RFA"
										className="nav-list-item-text"
										disableTypography
									/>
								</ListItemButton>
								<ListItemButton
									component={RouterLink}
									to="/my-time-entry"
									className="nav-list-item-button"
								>
									<ListItemIcon className="nav-list-item-icon">
										<AccessTimeIcon
											style={{ color: "white" }}
										/>
									</ListItemIcon>
									<ListItemText
										primary="My Time Entry"
										className="nav-list-item-text"
										disableTypography
									/>
								</ListItemButton>
							</>
						)}

						{!mobileOnly && (
							<>
								{/* PROPOSAL DROPDOWN */}
								<ListItemButton
									onClick={proposalDropdownClicked}
									className="nav-list-item-button-rfp"
								>
									<ListItemIcon className="nav-list-item-icon">
										<ArticleIcon
											id="nav-list-item-icon-image"
											style={{ color: "white" }}
										/>
									</ListItemIcon>
									<ListItemText
										primary="Proposals"
										id="proposalListItemText"
										className="nav-list-item-text"
										disableTypography
									/>
									<div className="dropdownIconContainer">
										{proposalsOpen ? (
											<ExpandLess
												style={{ color: "white" }}
											/>
										) : (
											<ExpandMore
												style={{ color: "white" }}
											/>
										)}
									</div>
								</ListItemButton>
								{/* PROPOSAL LINKS */}
								<Collapse
									in={proposalsOpen}
									timeout="auto"
									unmountOnExit
									id="nav-dropdown-collapse-rfp"
									className="rfp-related"
								>
									<ListItemButton
										component={RouterLink}
										to="/"
										className="nav-list-item-button-rfp"
									>
										<ListItemIcon
											id="rfpListIcon"
											className="nav-list-item-icon"
										>
											<CreateNewFolderIcon
												style={{ color: "white" }}
											/>
										</ListItemIcon>
										<ListItemText
											primary="RFP"
											id="rfpListItemText"
											className="nav-list-item-text"
											disableTypography
										/>
									</ListItemButton>
									<ListItemButton
										component={RouterLink}
										to="/rfa"
										className="nav-list-item-button"
									>
										<ListItemIcon
											id="rfaListIcon"
											className="nav-list-item-icon"
										>
											<ConstructionIcon
												style={{ color: "white" }}
											/>
										</ListItemIcon>
										<ListItemText
											primary="RFA"
											id="rfaListItemText"
											className="nav-list-item-text"
											disableTypography
										/>
									</ListItemButton>
									{props.accessibleTools.some(
										(t) =>
											t.name === "Product Library" ||
											t.name === "Admin",
									) && (
										<ListItemButton
											component={RouterLink}
											to="/proposals/product-library"
											className="nav-list-item-button"
										>
											<ListItemIcon className="nav-list-item-icon">
												<MenuBookIcon
													style={{ color: "white" }}
												/>
											</ListItemIcon>
											<ListItemText
												primary="Product Library"
												className="nav-list-item-text"
												disableTypography
											/>
										</ListItemButton>
									)}
									<ListItemButton
										component={RouterLink}
										to="/logs/rfp_user_form"
										className="nav-list-item-button"
									>
										<ListItemIcon
											id="rfpListIcon"
											className="nav-list-item-icon"
										>
											<TaskIcon
												style={{ color: "white" }}
											/>
										</ListItemIcon>
										<ListItemText
											primary="RFP Submission Logs"
											id="rfpListItemText"
											className="nav-list-item-text"
											disableTypography
										/>
									</ListItemButton>
									<ListItemButton
										component={RouterLink}
										to="/logs/rfa_user_form"
										className="nav-list-item-button"
									>
										<ListItemIcon
											id="rfaListIcon"
											className="nav-list-item-icon"
										>
											<TaskIcon
												style={{ color: "white" }}
											/>
										</ListItemIcon>
										<ListItemText
											primary="RFA Submission Logs"
											id="rfaListItemText"
											className="nav-list-item-text"
											disableTypography
										/>
									</ListItemButton>
								</Collapse>

								{/* MY TIME ENTRY */}
								<ListItemButton
									component={RouterLink}
									to="/my-time-entry"
									className="nav-list-item-button"
								>
									<ListItemIcon
										id="myTimeEntryIcon"
										className="nav-list-item-icon"
									>
										<AccessTimeIcon
											style={{ color: "white" }}
										/>
									</ListItemIcon>
									<ListItemText
										primary="My Time Entry"
										id="myTimeEntryText"
										className="nav-list-item-text"
										disableTypography
									/>
								</ListItemButton>

								{/* PROJECT COORDINATION */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.includes("Pending Jobs") ||
									props.accessibleTools
										.map((t) => t.name)
										.includes("Post Amendments") ||
									props.accessibleTools
										.map((t) => t.name)
										.includes("Admin") ||
									props.accessibleTools
										.map((t) => t.name)
										.includes("Archive Manager"),
								) && (
									<>
										<ListItemButton
											onClick={
												projectCoordinationDropdownClicked
											}
											className="nav-list-item-button"
										>
											<ListItemIcon id="nav-list-item-icon">
												<MergeIcon
													style={{ color: "white" }}
												/>
											</ListItemIcon>
											<ListItemText
												primary="Project Coordination"
												id="projectCoordinationListItemText"
												className="nav-list-item-text"
											/>
											<div className="dropdownIconContainer">
												{projectCoordinationOpen ? (
													<ExpandLess
														style={{
															color: "white",
														}}
													/>
												) : (
													<ExpandMore
														style={{
															color: "white",
														}}
													/>
												)}
											</div>
										</ListItemButton>
										{/* PROJECT COORDINATION LINKS */}
										<Collapse
											in={projectCoordinationOpen}
											timeout="auto"
											unmountOnExit
											sx={{ pl: "1em" }}
										>
											{Boolean(
												props.accessibleTools
													.map((t) => t.name)
													.includes("Pending Jobs"),
											) && (
												<React.Fragment>
													<ListItemButton
														component={RouterLink}
														to="/jobs/pending"
														className="nav-list-item-button"
													>
														<ListItemIcon
															id="pendingJobsIcon"
															className="nav-list-item-icon"
														>
															<NewReleasesIcon
																style={{
																	color: "white",
																}}
															/>
														</ListItemIcon>
														<ListItemText
															primary="Pending Jobs"
															id="pendingJobsText"
															className="nav-list-item-text"
															disableTypography
														/>
													</ListItemButton>
													<ListItemButton
														component={RouterLink}
														to="/jobs/amendments/pending"
														className="nav-list-item-button"
													>
														<ListItemIcon
															id="pendingAmendmentsIcon"
															className="nav-list-item-icon"
														>
															<PendingIcon
																style={{
																	color: "white",
																}}
															/>
														</ListItemIcon>
														<ListItemText
															primary="Pending Amendments"
															id="pendingAmendmentsText"
															className="nav-list-item-text"
															disableTypography
														/>
													</ListItemButton>
												</React.Fragment>
											)}
											{Boolean(
												props.accessibleTools
													.map((t) => t.name)
													.includes(
														"Post Amendments",
													),
											) && (
												<ListItemButton
													component={RouterLink}
													to="/jobs/amendments/post"
													className="nav-list-item-button"
												>
													<ListItemIcon
														id="postAmendmentsIcon"
														className="nav-list-item-icon"
													>
														<PublishedWithChangesIcon
															style={{
																color: "white",
															}}
														/>
													</ListItemIcon>
													<ListItemText
														primary="Post Amendments"
														id="postAmendmentsText"
														className="nav-list-item-text"
														disableTypography
													/>
												</ListItemButton>
											)}
											{Boolean(
												props.accessibleTools
													.map((t) => t.name)
													.includes(
														"Archive Manager",
													),
											) && (
												<ListItemButton
													component={RouterLink}
													to="/archives/manager"
													className="nav-list-item-button"
												>
													<ListItemIcon id="nav-list-item-icon">
														<SendAndArchiveIcon
															style={{
																color: "white",
															}}
														/>
													</ListItemIcon>
													<ListItemText
														primary="Archive Manager"
														id="archiveManagerText"
														className="nav-list-item-text"
													/>
												</ListItemButton>
											)}
										</Collapse>
									</>
								)}

								{/* PROJECT VIEWER */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.includes("Project Viewer"),
								) && (
									<ListItemButton
										component={RouterLink}
										to="/projects/viewer"
										className="nav-list-item-button"
									>
										<ListItemIcon
											id="projectViewerIcon"
											className="nav-list-item-icon"
										>
											<VisibilityIcon
												style={{ color: "white" }}
											/>
										</ListItemIcon>
										<ListItemText
											primary="Project Viewer"
											id="projectViewerText"
											className="nav-list-item-text"
											disableTypography
										/>
									</ListItemButton>
								)}

								{/* PROPERTY VIEWER */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.includes("Property Viewer"),
								) && (
									<ListItemButton
										component={RouterLink}
										to="/properties/viewer"
										className="nav-list-item-button"
									>
										<ListItemIcon
											id="propertyViewerIcon"
											className="nav-list-item-icon"
										>
											<DomainIcon
												style={{ color: "white" }}
											/>
										</ListItemIcon>
										<ListItemText
											primary="Property Viewer"
											id="propertyViewerText"
											className="nav-list-item-text"
											disableTypography
										/>
									</ListItemButton>
								)}

								{/* WORKLOAD VIEWER */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.includes("Workload Viewer"),
								) && (
									<ListItemButton
										component={RouterLink}
										to="/workload/viewer"
										className="nav-list-item-button"
									>
										<ListItemIcon
											id="workloadViewerIcon"
											className="nav-list-item-icon"
										>
											<WorkHistoryIcon
												style={{ color: "white" }}
											/>
										</ListItemIcon>
										<ListItemText
											primary="Workload Viewer"
											id="workloadViewerText"
											className="nav-list-item-text"
											disableTypography
										/>
									</ListItemButton>
								)}

								{/* PROJECT HEALTH DASHBOARD */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.includes("Project Health Dashboard"),
								) && (
									<ListItemButton
										component={RouterLink}
										to="/projects/health"
										className="nav-list-item-button"
									>
										<ListItemIcon
											id="projectHealthDashboardIcon"
											className="nav-list-item-icon"
										>
											<MonitorHeartIcon
												style={{ color: "white" }}
											/>
										</ListItemIcon>
										<ListItemText
											primary="Project Health Dashboard"
											id="projectHealthDashboardText"
											className="nav-list-item-text"
											disableTypography
										/>
									</ListItemButton>
								)}

								{/* PM PROJECT DASHBOARD */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.includes("PM Project Dashboard"),
								) && (
									<ListItemButton
										component={RouterLink}
										to={`/projects/pm-dashboard/${employeeID}`}
										className="nav-list-item-button"
									>
										<ListItemIcon
											id="pmProjectDashboardIcon"
											className="nav-list-item-icon"
										>
											<AssignmentIcon
												style={{ color: "white" }}
											/>
										</ListItemIcon>
										<ListItemText
											primary="PM Project Dashboard"
											id="pmProjectDashboardText"
											className="nav-list-item-text"
											disableTypography
										/>
									</ListItemButton>
								)}

								{/* DROPBOX ANALYZE */}
								<ListItemButton
									component={RouterLink}
									to="/dropbox_analyze"
									className="nav-list-item-button"
								>
									<ListItemIcon
										id="dropboxInterrogatorIcon"
										className="nav-list-item-icon"
									>
										<FindInPageIcon
											style={{ color: "white" }}
										/>
									</ListItemIcon>
									<ListItemText
										primary="Dropbox Analyzer"
										id="dropboxInterrogatorText"
										className="nav-list-item-text"
										disableTypography
									/>
								</ListItemButton>

									{/* Design */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.some((n) =>
											["Design Jobs Archive", "Spec Library", "Detail Library", "Core Repair Scopes", "Assemblies", "Design Categories", "Building Elements"].includes(n)
										),
								) && (
									<>
										<ListItemButton
											onClick={
												specificationsDropdownClicked
											}
											className="nav-list-item-button"
										>
											<ListItemIcon id="nav-list-item-icon">
												<HistoryEduIcon
													style={{ color: "white" }}
												/>
											</ListItemIcon>
											<ListItemText
												primary="Design"
												id="specificationsListItemText"
												className="nav-list-item-text"
											/>
											<div className="dropdownIconContainer">
												{specificationsOpen ? (
													<ExpandLess
														style={{
															color: "white",
														}}
													/>
												) : (
													<ExpandMore
														style={{
															color: "white",
														}}
													/>
												)}
											</div>
										</ListItemButton>
										{/* SPECIFICATIONS LINKS */}
										<Collapse
											in={specificationsOpen}
											timeout="auto"
											unmountOnExit
											sx={{ pl: "1em" }}
										>
											{Boolean(
												props.accessibleTools
													.map((t) => t.name)
													.includes("Design Jobs Archive"),
											) && (
												<ListItemButton
													component={RouterLink}
													to="/design/design_jobs_archive"
													className="nav-list-item-button"
												>
													<ListItemIcon id="nav-list-item-icon">
														<HouseSidingIcon
															style={{
																color: "white",
															}}
														/>
													</ListItemIcon>
													<ListItemText
														primary="Design Jobs Archive"
														id="designJobsText"
														className="nav-list-item-text"
													/>
												</ListItemButton>
											)}
											<ListItemButton
												component={RouterLink}
												to="/design/spec_library"
												className="nav-list-item-button"
											>
												<ListItemIcon id="nav-list-item-icon">
													<MenuBookIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Spec Library"
													id="specLibraryText"
													className="nav-list-item-text"
												/>
											</ListItemButton>
											{Boolean(
												props.accessibleTools?.find(
													(t) =>
														t.name ===
														"Spec Library",
												)?.admin_access,
											) && (
												<>
													<ListItemButton
														component={RouterLink}
														to="/design/pending_spec_changes"
														className="nav-list-item-button"
													>
														<ListItemIcon id="nav-list-item-icon">
															<ReviewsIcon
																style={{
																	color: "white",
																}}
															/>
														</ListItemIcon>
														<ListItemText
															primary="Pending Spec Changes"
															id="pendingChangesText"
															className="nav-list-item-text"
														/>
													</ListItemButton>
													<ListItemButton
														component={RouterLink}
														to="/design/spec_change_log"
														className="nav-list-item-button"
													>
														<ListItemIcon id="nav-list-item-icon">
															<AutoStoriesIcon
																style={{
																	color: "white",
																}}
															/>
														</ListItemIcon>
														<ListItemText
															primary="Spec Change Log"
															id="specChangeLogText"
															className="nav-list-item-text"
														/>
													</ListItemButton>
													<ListItemButton
														component={RouterLink}
														to="/design/design_job_change_log"
														className="nav-list-item-button"
													>
														<ListItemIcon id="nav-list-item-icon">
															<PublishedWithChangesIcon
																style={{
																	color: "white",
																}}
															/>
														</ListItemIcon>
														<ListItemText
															primary="Design Job Change Log"
															id="designJobChangeLogText"
															className="nav-list-item-text"
														/>
													</ListItemButton>
												</>
											)}
											{Boolean(
												props.accessibleTools
													.map((t) => t.name)
													.includes("Detail Library"),
											) && (
												<ListItemButton
													component={RouterLink}
													to="/design/detail_library"
													className="nav-list-item-button"
												>
													<ListItemIcon id="nav-list-item-icon">
														<CollectionsBookmarkIcon
															style={{ color: "white" }}
														/>
													</ListItemIcon>
													<ListItemText
														primary="Detail Library"
														id="detailLibraryText"
														className="nav-list-item-text"
													/>
												</ListItemButton>
											)}
											{Boolean(
												props.accessibleTools
													.map((t) => t.name)
													.includes("Core Repair Scopes"),
											) && (
												<ListItemButton
													component={RouterLink}
													to="/design/core_repair_scopes"
													className="nav-list-item-button"
												>
													<ListItemIcon id="nav-list-item-icon">
														<BuildIcon
															style={{ color: "white" }}
														/>
													</ListItemIcon>
													<ListItemText
														primary="Core Repair Scopes"
														id="crsText"
														className="nav-list-item-text"
													/>
												</ListItemButton>
											)}
											{Boolean(
												props.accessibleTools
													.map((t) => t.name)
													.includes("Assemblies"),
											) && (
												<ListItemButton
													component={RouterLink}
													to="/design/assemblies"
													className="nav-list-item-button"
												>
													<ListItemIcon id="nav-list-item-icon">
														<ViewModuleIcon
															style={{ color: "white" }}
														/>
													</ListItemIcon>
													<ListItemText
														primary="Assemblies"
														id="assembliesText"
														className="nav-list-item-text"
													/>
												</ListItemButton>
											)}
											{Boolean(
												props.accessibleTools
													.map((t) => t.name)
													.includes("Design Categories"),
											) && (
												<ListItemButton
													component={RouterLink}
													to="/design/design_categories"
													className="nav-list-item-button"
												>
													<ListItemIcon id="nav-list-item-icon">
														<CategoryIcon
															style={{ color: "white" }}
														/>
													</ListItemIcon>
													<ListItemText
														primary="Design Categories"
														id="designCategoriesText"
														className="nav-list-item-text"
													/>
												</ListItemButton>
											)}
											{Boolean(
												props.accessibleTools
													.map((t) => t.name)
													.includes("Building Elements"),
											) && (
												<ListItemButton
													component={RouterLink}
													to="/design/building_elements"
													className="nav-list-item-button"
												>
													<ListItemIcon id="nav-list-item-icon">
														<LayersIcon
															style={{ color: "white" }}
														/>
													</ListItemIcon>
													<ListItemText
														primary="Building Elements"
														id="buildingElementsText"
														className="nav-list-item-text"
													/>
												</ListItemButton>
											)}
										</Collapse>
									</>
								)}

								{/* HARDWARE INVENTORY */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.includes("Hardware Inventory"),
								) && (
									<>
										<ListItemButton
											onClick={
												hardwareInventoryDropdownClicked
											}
											className="nav-list-item-button"
										>
											<ListItemIcon id="nav-list-item-icon">
												<InventoryIcon
													style={{ color: "white" }}
												/>
											</ListItemIcon>
											<ListItemText
												primary="Hardware Inventory"
												id="hardwareInventoryListItemText"
												className="nav-list-item-text"
											/>
											<div className="dropdownIconContainer">
												{hardwareInventoryOpen ? (
													<ExpandLess
														style={{
															color: "white",
														}}
													/>
												) : (
													<ExpandMore
														style={{
															color: "white",
														}}
													/>
												)}
											</div>
										</ListItemButton>
										{/* HARDWARE INVENTORY LINKS */}
										<Collapse
											in={hardwareInventoryOpen}
											timeout="auto"
											unmountOnExit
											sx={{ pl: "1em" }}
										>
											<ListItemButton
												component={RouterLink}
												to="/hardware_inventory/devices"
												className="nav-list-item-button"
											>
												<ListItemIcon id="nav-list-item-icon">
													<LaptopMacIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Devices"
													id="DevicesInventoryText"
													className="nav-list-item-text"
												/>
											</ListItemButton>

											<ListItemButton
												component={RouterLink}
												to="/hardware_inventory/leases"
												className="nav-list-item-button"
											>
												<ListItemIcon id="nav-list-item-icon">
													<LowPriorityIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Leases"
													id="LeasesInventoryText"
													className="nav-list-item-text"
												/>
											</ListItemButton>

											<ListItemButton
												component={RouterLink}
												to="/hardware_inventory/service_history"
												className="nav-list-item-button"
											>
												<ListItemIcon id="nav-list-item-icon">
													<HardwareIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Service History"
													id="ServiceHistoryInventoryText"
													className="nav-list-item-text"
												/>
											</ListItemButton>
										</Collapse>
									</>
								)}

								{/* LOGS */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.includes("Logs"),
								) && (
									<>
										{/* LOGS DROPDOWN */}
										<ListItemButton
											onClick={logsDropdownClicked}
											className="nav-list-item-button"
										>
											<ListItemIcon className="nav-list-item-icon">
												<DensitySmallIcon
													style={{ color: "white" }}
												/>
											</ListItemIcon>
											<ListItemText
												primary="Logs"
												id="logsListItemText"
												className="nav-list-item-text"
												disableTypography
											/>
											<div className="dropdownIconContainer">
												{logsOpen ? (
													<ExpandLess
														style={{
															color: "white",
														}}
													/>
												) : (
													<ExpandMore
														style={{
															color: "white",
														}}
													/>
												)}
											</div>
										</ListItemButton>
										{/* LOGS LINKS */}
										<Collapse
											in={logsOpen}
											timeout="auto"
											unmountOnExit
											id="nav-dropdown-collapse"
										>

											<ListItemButton
												component={RouterLink}
												to="/logs/automation/dashboard"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="automationLogsDashboardIcon"
													className="nav-list-item-icon"
												>
													<QueryStatsIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Automation Logs"
													id="automationLogsDashboardText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/logs/access"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="accessLogIcon"
													className="nav-list-item-icon"
												>
													<BadgeIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Access Logs"
													id="accessLogText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/logs/rfp_form"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="rfpFormLogIcon"
													className="nav-list-item-icon"
												>
													<TaskIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="RFP Form Logs"
													id="rfpFormLogText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>

											<ListItemButton
												component={RouterLink}
												to="/logs/rfa_form"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="rfaFormLogIcon"
													className="nav-list-item-icon"
												>
													<TaskIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="RFA Form Logs"
													id="rfaFormLogText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>

											<ListItemButton
												component={RouterLink}
												to="/logs/pending_job_form"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="pendingJobFormLogIcon"
													className="nav-list-item-icon"
												>
													<TaskIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Pending Job Form Logs"
													id="pendingJobFormLogText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>

											<ListItemButton
												component={RouterLink}
												to="/logs/pending_amendment_form"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="pendingAmendmentFormLogIcon"
													className="nav-list-item-icon"
												>
													<TaskIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Pending Amendment Form Logs"
													id="pendingAmendmentFormLogText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
										</Collapse>
									</>
								)}

								{/* ADMIN */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.includes("Admin"),
								) && (
									<>
										{/* ADMIN DROPDOWN */}
										<ListItemButton
											onClick={adminDropdownClicked}
											className="nav-list-item-button"
										>
											<ListItemIcon className="nav-list-item-icon">
												<AdminPanelSettingsIcon
													style={{ color: "white" }}
												/>
											</ListItemIcon>
											<ListItemText
												primary="Admin"
												id="adminListItemText"
												className="nav-list-item-text"
												disableTypography
											/>
											<div className="dropdownIconContainer">
												{adminOpen ? (
													<ExpandLess
														style={{
															color: "white",
														}}
													/>
												) : (
													<ExpandMore
														style={{
															color: "white",
														}}
													/>
												)}
											</div>
										</ListItemButton>
										{/* ADMIN LINKS */}
										<Collapse
											in={adminOpen}
											timeout="auto"
											unmountOnExit
											id="nav-dropdown-collapse"
										>
											<ListItemButton
												component={RouterLink}
												to="/admin/category_console"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="categoryConsoleIcon"
													className="nav-list-item-icon"
												>
													<CategoryIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Category Console"
													id="categoryConsoleText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/admin/employee_viewer"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="employeeViewerIcon"
													className="nav-list-item-icon"
												>
													<BadgeIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Employee Viewer"
													id="employeeViewerText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/admin/role_console"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="roleConsoleIcon"
													className="nav-list-item-icon"
												>
													<NoEncryptionGmailerrorredIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Role Console"
													id="roleConsoleText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/admin/job_titles_console"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="jobTitlesConsoleIcon"
													className="nav-list-item-icon"
												>
													<WorkIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Job Titles Console"
													id="jobTitlesConsoleText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/admin/property_console"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="propertyConsoleIcon"
													className="nav-list-item-icon"
												>
													<HomeWorkIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Property Console"
													id="propertyConsoleText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/admin/project_console"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="projectConsoleIcon"
													className="nav-list-item-icon"
												>
													<DriveFileRenameOutlineIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Project Console"
													id="projectConsoleText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/admin/phase_console"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="phaseConsoleIcon"
													className="nav-list-item-icon"
												>
													<ScatterPlotIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Phase Console"
													id="phaseConsoleText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/admin/device_status_console"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="deviceStatusConsoleIcon"
													className="nav-list-item-icon"
												>
													<ImportantDevicesIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Device Status Console"
													id="deviceStatusConsoleText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/admin/workload_viewer_console"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="workloadViewerConsoleIcon"
													className="nav-list-item-icon"
												>
													<WorkHistoryIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Workload Viewer Console"
													id="workloadViewerConsoleText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/admin/unit_mining_dashboard"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="unitMiningDashboardIcon"
													className="nav-list-item-icon"
												>
													<TravelExploreIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Unit Mining Dashboard"
													id="unitMiningDashboardText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
											<ListItemButton
												component={RouterLink}
												to="/admin/time_off_console"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="timeOffConsoleIcon"
													className="nav-list-item-icon"
												>
													<EventBusyIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Time Off Console"
													id="timeOffConsoleText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
										</Collapse>
									</>
								)}

								{/* Profile */}
								{Boolean(
									props.accessibleTools
										.map((t) => t.name)
										.includes("Email Signature"),
								) && (
									<>
										{/* PROFILE DROPDOWN */}
										<ListItemButton
											onClick={profileDropdownClicked}
											className="nav-list-item-button"
										>
											<ListItemIcon className="nav-list-item-icon">
												<ManageAccountsIcon
													style={{ color: "white" }}
												/>
											</ListItemIcon>
											<ListItemText
												primary="Profile"
												id="profileListItemText"
												className="nav-list-item-text"
												disableTypography
											/>
											<div className="dropdownIconContainer">
												{profileOpen ? (
													<ExpandLess
														style={{
															color: "white",
														}}
													/>
												) : (
													<ExpandMore
														style={{
															color: "white",
														}}
													/>
												)}
											</div>
										</ListItemButton>
										{/* PROFILE LINKS */}
										<Collapse
											in={profileOpen}
											timeout="auto"
											unmountOnExit
											id="nav-dropdown-collapse"
										>
											<ListItemButton
												component={RouterLink}
												to="/profile/signature"
												className="nav-list-item-button"
											>
												<ListItemIcon
													id="mailSignatureIcon"
													className="nav-list-item-icon"
												>
													<AttachEmailIcon
														style={{
															color: "white",
														}}
													/>
												</ListItemIcon>
												<ListItemText
													primary="Email Signature"
													id="emailSignatureText"
													className="nav-list-item-text"
													disableTypography
												/>
											</ListItemButton>
										</Collapse>
									</>
								)}
							</>
						)}
					</div>
					{!mobileOnly && (
						<div id="nav-footer">
							<ListItemButton
								onClick={signOutPressed}
								className="nav-list-item-button"
							>
								<ListItemIcon className="nav-list-item-icon">
									<LogoutIcon style={{ color: "white" }} />
								</ListItemIcon>
								<ListItemText
									primary="Sign Out"
									primaryTypographyProps={{
										style: { fontWeight: 500 },
									}}
									className="nav-list-item-text"
									disableTypography
									id="signOutListItemText"
								/>
							</ListItemButton>
						</div>
					)}
					{mobileOnly && (
						<div id="nav-footer">
							<ListItemButton
								onClick={signOutPressed}
								className="nav-list-item-button"
							>
								<ListItemIcon className="nav-list-item-icon">
									<LogoutIcon style={{ color: "white" }} />
								</ListItemIcon>
								<ListItemText
									primary="Sign Out"
									primaryTypographyProps={{
										style: { fontWeight: 500 },
									}}
									className="nav-list-item-text"
									disableTypography
									id="signOutListItemText"
								/>
							</ListItemButton>
						</div>
					)}
				</List>
			</div>
		</Collapse>
	);
};

export default Navigation;
